/**
 * Rebate Matching Utility
 *
 * Handles matching rebates to system options based on complex filtering rules:
 * - System type compatibility
 * - Option number restrictions
 * - Jurisdiction/geographic matching
 * - Eligibility criteria (SEER2, AFUE, income, home size, etc.)
 * - Stackability rules
 * - Expiration tracking and urgency levels
 *
 * @module rebateMatching
 */

/* =========================
   Date/Expiration Utilities
   ========================= */

/**
 * Calculate days until rebate expiration
 * @param {string|Date} endDate - Rebate end date
 * @returns {number} Days until expiration (negative if expired)
 */
export function calculateDaysUntilExpiration(endDate) {
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day
  end.setHours(0, 0, 0, 0);

  const diffTime = end - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Get urgency level based on days until expiration
 * @param {number} daysUntilExpiration - Days until rebate expires
 * @returns {Object} Urgency level with color, label, and priority
 */
export function getUrgencyLevel(daysUntilExpiration) {
  if (daysUntilExpiration < 0) {
    return {
      level: 'expired',
      color: '#94a3b8',  // Gray
      label: 'Expired',
      priority: 0
    };
  } else if (daysUntilExpiration <= 30) {
    return {
      level: 'critical',
      color: '#ef4444',  // Red
      label: 'Expires Soon',
      priority: 3
    };
  } else if (daysUntilExpiration <= 90) {
    return {
      level: 'warning',
      color: '#f59e0b',  // Yellow/Orange
      label: 'Expiring',
      priority: 2
    };
  } else {
    return {
      level: 'normal',
      color: '#10b981',  // Green
      label: 'Available',
      priority: 1
    };
  }
}

/* =========================
   Eligibility Checking
   ========================= */

/**
 * Check if system option meets rebate eligibility rules
 * @param {Object} eligibilityRules - JSONB eligibility rules from rebate
 * @param {Object} systemOption - System option being checked
 * @param {Object} assessmentData - Assessment data (for home size, income, etc.)
 * @returns {Object} { eligible: boolean, failedRules: string[] }
 */
export function checkEligibilityRules(eligibilityRules, systemOption, assessmentData = {}) {
  const failedRules = [];

  // No rules = eligible
  if (!eligibilityRules || Object.keys(eligibilityRules).length === 0) {
    return { eligible: true, failedRules: [] };
  }

  // Check minimum SEER2 requirement
  if (eligibilityRules.minSEER2 && systemOption.seer2_rating < eligibilityRules.minSEER2) {
    failedRules.push(`Requires SEER2 ≥ ${eligibilityRules.minSEER2} (system has ${systemOption.seer2_rating})`);
  }

  // Check minimum AFUE requirement (for gas systems)
  if (eligibilityRules.minAFUE && systemOption.afue_rating && systemOption.afue_rating < eligibilityRules.minAFUE) {
    failedRules.push(`Requires AFUE ≥ ${eligibilityRules.minAFUE}% (system has ${systemOption.afue_rating}%)`);
  }

  // Check maximum home size
  if (eligibilityRules.maxHomeSize && assessmentData.squareFootage > eligibilityRules.maxHomeSize) {
    failedRules.push(`Home too large (max ${eligibilityRules.maxHomeSize} sq ft)`);
  }

  // Check minimum home size
  if (eligibilityRules.minHomeSize && assessmentData.squareFootage < eligibilityRules.minHomeSize) {
    failedRules.push(`Home too small (min ${eligibilityRules.minHomeSize} sq ft)`);
  }

  // Check income limits (if provided in assessment)
  if (eligibilityRules.maxIncome && assessmentData.householdIncome) {
    if (assessmentData.householdIncome > eligibilityRules.maxIncome) {
      failedRules.push(`Income exceeds limit ($${eligibilityRules.maxIncome.toLocaleString()})`);
    }
  }

  // Check if replacing specific equipment type
  if (eligibilityRules.replacingType && assessmentData.systemType !== eligibilityRules.replacingType) {
    failedRules.push(`Must be replacing ${eligibilityRules.replacingType}`);
  }

  // Check Energy Star requirement
  if (eligibilityRules.energyStarRequired && systemOption.seer2_rating < 15) {
    failedRules.push('Must meet Energy Star requirements (SEER2 ≥ 15)');
  }

  // Check if condensing furnace required (for gas systems)
  if (eligibilityRules.condensingRequired && !systemOption.is_condensing) {
    failedRules.push('Must be condensing furnace (90%+ AFUE)');
  }

  // Check if must be complete system replacement (not just condenser)
  if (eligibilityRules.completeSystemRequired && assessmentData.replacementContext === 'condenser_only') {
    failedRules.push('Must replace complete system (indoor + outdoor units)');
  }

  return {
    eligible: failedRules.length === 0,
    failedRules
  };
}

/* =========================
   Stackability Rules
   ========================= */

/**
 * Apply stackability rules to filter rebates that can be combined
 * @param {Array} matchedRebates - Array of rebates that passed initial filters
 * @returns {Array} Rebates organized into stackable groups
 */
export function applyStackabilityRules(matchedRebates) {
  // If no rebates, return empty array
  if (!matchedRebates || matchedRebates.length === 0) {
    return [];
  }

  // Separate stackable and non-stackable rebates
  const stackable = matchedRebates.filter(r => r.stackability_rules?.stackable !== false);
  const nonStackable = matchedRebates.filter(r => r.stackability_rules?.stackable === false);

  // Build stackable groups
  const stackableGroups = [];

  // Start with highest priority rebates
  const sortedStackable = [...stackable].sort((a, b) => {
    // Sort by display_priority first (1 = high, 3 = low)
    if (a.display_priority !== b.display_priority) {
      return a.display_priority - b.display_priority;
    }
    // Then by amount (highest first)
    return b.amount - a.amount;
  });

  // Build compatible groups
  for (const rebate of sortedStackable) {
    let addedToGroup = false;

    // Try to add to existing group
    for (const group of stackableGroups) {
      if (canStackWithGroup(rebate, group)) {
        group.push(rebate);
        addedToGroup = true;
        break;
      }
    }

    // Create new group if couldn't add to existing
    if (!addedToGroup) {
      stackableGroups.push([rebate]);
    }
  }

  // Calculate totals for each group
  const groupsWithTotals = stackableGroups.map(group => ({
    rebates: group,
    totalAmount: group.reduce((sum, r) => sum + r.amount, 0),
    count: group.length
  }));

  // Add non-stackable rebates as individual groups
  const nonStackableGroups = nonStackable.map(rebate => ({
    rebates: [rebate],
    totalAmount: rebate.amount,
    count: 1
  }));

  // Combine and sort by total amount (highest first)
  const allGroups = [...groupsWithTotals, ...nonStackableGroups].sort(
    (a, b) => b.totalAmount - a.totalAmount
  );

  return allGroups;
}

/**
 * Check if a rebate can be stacked with an existing group
 * @param {Object} rebate - Rebate to check
 * @param {Array} group - Existing group of rebates
 * @returns {boolean} True if rebate can be added to group
 */
function canStackWithGroup(rebate, group) {
  const rules = rebate.stackability_rules || {};

  // Check if rebate has specific allowed_with list
  if (rules.allowed_with && rules.allowed_with.length > 0) {
    // Must be in allowed_with list for every rebate in group
    return group.every(r => rules.allowed_with.includes(r.id));
  }

  // Check if rebate has not_allowed_with list
  if (rules.not_allowed_with && rules.not_allowed_with.length > 0) {
    // Must NOT be in not_allowed_with list for any rebate in group
    return !group.some(r => rules.not_allowed_with.includes(r.id));
  }

  // Check if any rebate in group has restrictions against this rebate
  for (const groupRebate of group) {
    const groupRules = groupRebate.stackability_rules || {};

    // If group rebate has allowed_with, new rebate must be in it
    if (groupRules.allowed_with && groupRules.allowed_with.length > 0) {
      if (!groupRules.allowed_with.includes(rebate.id)) {
        return false;
      }
    }

    // If group rebate has not_allowed_with, new rebate must NOT be in it
    if (groupRules.not_allowed_with && groupRules.not_allowed_with.length > 0) {
      if (groupRules.not_allowed_with.includes(rebate.id)) {
        return false;
      }
    }
  }

  return true;
}

/* =========================
   Main Matching Function
   ========================= */

/**
 * Match rebates for a specific system option
 * @param {Object} params - Matching parameters
 * @returns {Array} Stackable groups of matched rebates sorted by total value
 */
export function matchRebatesForOption(params) {
  const {
    systemOption,        // System option object from database
    allRebates,          // All available rebates
    assessmentData,      // Assessment data (system type, square footage, etc.)
    contractorJurisdictions = []  // Contractor's configured jurisdictions
  } = params;

  // Filter rebates through multiple stages
  let matchedRebates = allRebates.filter(rebate => {
    // Stage 1: Status check - must be active
    if (rebate.status !== 'active') {
      return false;
    }

    // Stage 2: Date check - must not be expired
    const daysUntilExpiration = calculateDaysUntilExpiration(rebate.end_date);
    if (daysUntilExpiration < 0) {
      return false;
    }

    // Stage 3: System type check
    // If rebate has applicable_system_types, system must be in list
    if (rebate.applicable_system_types && rebate.applicable_system_types.length > 0) {
      if (!rebate.applicable_system_types.includes(assessmentData.systemType)) {
        return false;
      }
    }

    // Stage 4: System option number check
    // If rebate has applicable_system_options, option number must be in list
    if (rebate.applicable_system_options && rebate.applicable_system_options.length > 0) {
      if (!rebate.applicable_system_options.includes(systemOption.option_number)) {
        return false;
      }
    }

    // Stage 5: Jurisdiction check
    // If rebate has jurisdiction and contractor has jurisdictions, must match
    if (rebate.jurisdiction) {
      // If contractor has no jurisdictions configured, assume all qualify
      if (contractorJurisdictions.length === 0) {
        // Allow it through - contractor hasn't configured jurisdictions
      } else {
        // Check if rebate's jurisdiction matches any contractor jurisdiction
        const jurisdictionMatch = contractorJurisdictions.some(cj =>
          cj.name === rebate.jurisdiction ||
          cj.parent_jurisdiction === rebate.jurisdiction
        );
        if (!jurisdictionMatch) {
          return false;
        }
      }
    }

    // Stage 6: Eligibility rules check
    const eligibility = checkEligibilityRules(
      rebate.eligibility_rules,
      systemOption,
      assessmentData
    );
    if (!eligibility.eligible) {
      return false;
    }

    return true;
  });

  // Enrich rebates with urgency data
  matchedRebates = matchedRebates.map(rebate => {
    const daysUntilExpiration = calculateDaysUntilExpiration(rebate.end_date);
    const urgency = getUrgencyLevel(daysUntilExpiration);

    return {
      ...rebate,
      daysUntilExpiration,
      urgencyLevel: urgency.level,
      urgencyColor: urgency.color,
      urgencyLabel: urgency.label,
      urgencyPriority: urgency.priority
    };
  });

  // Apply stackability rules and return groups
  const stackableGroups = applyStackabilityRules(matchedRebates);

  return stackableGroups;
}

/* =========================
   Utility Functions
   ========================= */

/**
 * Format rebate amount for display
 * @param {Object} rebate - Rebate object
 * @returns {string} Formatted amount string
 */
export function formatRebateAmount(rebate) {
  if (rebate.amount_type === 'percentage') {
    return `${rebate.amount}%`;
  } else if (rebate.amount_type === 'tiered') {
    return 'Up to $' + rebate.amount.toLocaleString();
  } else {
    return '$' + rebate.amount.toLocaleString();
  }
}

/**
 * Get category display label
 * @param {string} category - Rebate category key
 * @returns {string} Display label
 */
export function getCategoryLabel(category) {
  const labels = {
    federal: 'Federal Tax Credit',
    state: 'State Rebate',
    county: 'County Rebate',
    city: 'City Rebate',
    utility: 'Utility Rebate',
    manufacturer: 'Manufacturer Rebate',
    contractor_promotion: 'Special Promotion',
    other: 'Other Incentive'
  };
  return labels[category] || category;
}

/**
 * Calculate total savings from a rebate group
 * @param {Object} group - Rebate group from applyStackabilityRules
 * @returns {number} Total rebate amount
 */
export function calculateGroupTotal(group) {
  return group.rebates.reduce((sum, rebate) => sum + rebate.amount, 0);
}
